import { useEffect, useRef, useState, type DragEvent } from 'react';
import { getProjectFile, PROJECT_FILE_ACCEPT, type ProjectFile } from '@/api';

type Props = {
  projectId: string | null;
  files: ProjectFile[];
  busy?: boolean;
  error?: string | null;
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
};

function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ProjectContextFiles({ projectId, files, busy, error, onAdd, onRemove }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preview, setPreview] = useState<string>('');

  useEffect(() => {
    if (!projectId || !selectedId) {
      setPreview('');
      return;
    }
    let cancelled = false;
    getProjectFile(projectId, selectedId)
      .then((f) => {
        if (!cancelled) setPreview(f.extracted_text || '');
      })
      .catch(() => {
        if (!cancelled) setPreview('');
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, selectedId]);

  const takeFiles = (list: FileList | File[]) => {
    const next = Array.from(list).filter((f) => f.size > 0);
    if (next.length) onAdd(next);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (!projectId || busy) return;
    takeFiles(e.dataTransfer.files);
  };

  return (
    <div data-testid="project-context" className="shrink-0 border-b border-border">
      <div className="px-5 py-3 flex items-center gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-textp">Project context</div>
          <div className="text-[11px] text-texts">
            {projectId
              ? files.length === 0
                ? 'Included in every Chat and Run'
                : `${files.length} file${files.length === 1 ? '' : 's'} included in every Chat and Run`
              : 'Open a project to attach files'}
          </div>
        </div>
        <button
          type="button"
          data-testid="project-context-add"
          disabled={!projectId || !!busy}
          onClick={() => input.current?.click()}
          className="ml-auto shrink-0 flex items-center gap-1.5 bg-surface hover:bg-surface2 disabled:opacity-50 border border-border text-textp text-xs font-medium px-3 py-1.5 rounded-md whitespace-nowrap"
        >
          <i className="ri-file-add-line" /> Add files
        </button>
        <input
          ref={input}
          type="file"
          multiple
          accept={PROJECT_FILE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            takeFiles(e.target.files ?? []);
            e.target.value = '';
          }}
        />
      </div>

      <div
        data-testid="project-context-drop"
        onDragOver={(e) => {
          e.preventDefault();
          if (projectId && !busy) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`mx-5 mb-3 rounded-md border border-dashed px-3 py-3 ${dragOver ? 'border-accent bg-accent/5' : 'border-border'}`}
      >
        {!projectId ? (
          <div className="text-xs text-texts text-center py-2">Save or open a project to attach context files.</div>
        ) : files.length === 0 ? (
          <div className="text-xs text-texts text-center py-2">
            Drop pdf, markdown, csv, json, or source files here.
          </div>
        ) : (
          <ul className="space-y-1">
            {files.map((f) => (
              <li
                key={f.id}
                className={`flex items-center gap-2 rounded-md px-2 py-1.5 group ${selectedId === f.id ? 'bg-surface2' : 'hover:bg-surface2'}`}
              >
                <button
                  type="button"
                  className="flex items-center gap-2 min-w-0 flex-1 text-left"
                  onClick={() => setSelectedId(f.id)}
                >
                  <i className="ri-file-text-line text-texts text-sm shrink-0" />
                  <span className="text-xs text-textp truncate font-mono">{f.name}</span>
                  <span className="text-[10px] font-mono text-texts shrink-0">{formatSize(f.size_bytes)}</span>
                </button>
                <button
                  type="button"
                  title="Remove"
                  data-testid="project-context-remove"
                  onClick={() => {
                    if (selectedId === f.id) setSelectedId(null);
                    onRemove(f.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 text-texts hover:text-error shrink-0"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {busy && <div className="text-[11px] text-texts mt-2">Uploading…</div>}
        {error && <div className="text-[11px] text-error mt-2">{error}</div>}
        {selectedId && preview !== '' && (
          <pre className="mt-2 max-h-40 overflow-auto p-2 bg-surface/60 rounded-md font-mono text-[11px] text-textp whitespace-pre-wrap">
            {preview}
          </pre>
        )}
      </div>
    </div>
  );
}
